class UserClass {
  localStorageKey = 'user';

  /**
   * Helper function to check local and session storage whether a `user` exists.
   *
   * @returns {boolean} True if user exists in storage.
   */
  exists() {
    return localStorage.getItem(this.localStorageKey) || sessionStorage.getItem(this.localStorageKey);
  }

  /**
   * Helper function to check local and session storage for a `user`.
   *
   * @returns {(null|object)} User JSON data or null if user doesn’t exist.
   */
  getUser() {
    if (!this.exists()) return null;

    return JSON.parse(localStorage.getItem(this.localStorageKey) ?? sessionStorage.getItem(this.localStorageKey));
  }

  /**
   * Helper function to retrieve a property of the user storage object (if it exists).
   *
   * @param {string} name of the property to check for in the user object.
   *
   * @returns {(null|number|string|object)} Returns the object property, which can be one of several types.
   */
  getProperty(name) {
    if (!this.exists()) return null;

    const user = this.getUser();

    return user[name];
  }

  /**
   * Determines is a user is valid:
   * i.e. That they both exist and haven't 'expired'.
   *
   * @returns {boolean} True if valid user, false if not for any reason.
   */
  isValid() {
    return this.exists() && !this.isExpired();
  }

  /**
   * Returns whether or not the user's validity has expired.
   *
   * @returns {boolean} True if user's validity has expired.
   */
  isExpired() {
    const today = new Date().toISOString();

    // User's profile has an expiry date and it's in the past relative to `today`.
    if (this.getExpiry() && this.getExpiry() < today) {
      return true;
    }

    // Either user's profile doesn't have expiry date (session storage) or it's in the future relative to `today`.
    return false;
  }

  /**
   * Returns user's age.
   *
   * @returns {number} User's age as whole number (i.e., years).
   */
  getAge() {
    return this.getProperty('age');
  }

  /**
   * Returns user's created-at date as ISO string.
   *
   * @returns {string} ISO string representing date/time that the user was stored.
   */
  getCreated() {
    return this.getProperty('saved');
  }

  /**
   * Returns user's DOB as an object, with properties of dd, mm, and yy.
   *
   * @returns {object} Object with date (dd), month (mm), year (yy) properties.
   */
  getDOB() {
    return this.getProperty('dob');
  }

  /**
   * Returns user's 'expiry' date as ISO string.
   * i.e., if the user chose to be remembered for 30 days, this is the end of that period.
   *
   * @returns {string} ISO string representing date/time that the user's validity will expire.
   */
  getExpiry() {
    return this.getProperty('expires');
  }

  /**
   * Returns user's province as two-character abbreviation, lowercase.
   *
   * @returns {string} Province abbreviation.
   */
  getProvince() {
    return this.getProperty('province');
  }

  /**
   * Returns whether or not the user chose to be 'remembered' for 30 day'.
   *
   * @returns {boolean} True if user checked 'remember' checkbo'.
   */
  getRememberMe() {
    return this.getProperty('remember');
  }

  /**
   * Calculate user's DOB as an object, with properties of dd, mm, and yy.
   *
   * @param {string} dateString ISO date string of the user's birthdate.
   *
   * @returns {(null|object)} Resaves user (with updated birthdate, if valid ISO date was provided).
   */
  setDOB(dateString) {
    if (!dateString) return null;
    let dob = null;
    const birthDate = new Date(dateString);

    if (birthDate.getDate()) {
      dob = {
        dd: birthDate.getDate(),
        mm: birthDate.getMonth(),
        yy: birthDate.getFullYear(),
      };
    }

    return dob;
  }

  /**
   * Calculate an age by substracting a DOB's year from the current year.
   * (And adjusting based on month, as needed.)
   *
   * @param {string} dateString ISO date string of the user's birthdate.
   *
   * @returns {number} User's age in years, expressed as whole number.
   */
  calculateAge(dateString) {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  /**
   * Creates an object of relevant values found in the from argument.
   *
   * @param {object} formValues Age-gate form values from which the user's DOB can be read.
   *
   * @returns {number} The user's age in years.
   */
  formatDOB(dd, mm, yyyy) {
    // FYI: Months are zero-based, cf. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date, hence subtracting 1.
    const dateString = new Date(yyyy, mm - 1, dd);

    return dateString;
  }

  /**
   * Prepares the user object for local or session storage.
   *
   * @param {object} data Object of data points required to build user object (dob, province, remember).
   *
   * @returns {object} The user object to be stored in local/session storage.
   */
  prepareUserForStorage(data) {
    const dob = this.formatDOB(data.date, data.month, data.year);

    const user = {
      age: User.calculateAge(dob),
      dob: User.setDOB(dob),
      province: data.province,
      saved: new Date().toISOString(),
      expires: null,
      remember: false,
    };

    if (data.remember) {
      let expiryDate = new Date();

      expiryDate.setDate(expiryDate.getDate() + 30);

      user.expires = expiryDate;
      user.remember = true;
    }

    return user;
  }

  /**
   * Prepares the user object for local or session storage.
   *
   * @param {object} data Object of data points required to build user object (dob, province, remember).
   */
  storeUser(data) {
    const user = this.prepareUserForStorage(data);

    if (data.remember) {
      localStorage.setItem(this.localStorageKey, JSON.stringify(user));
    } else {
      sessionStorage.setItem(this.localStorageKey, JSON.stringify(user));
    }
  }
}

const User = new UserClass();

export default User;

window.User = User;
